import { useState } from 'react'
import {
  ChevronRight,
  ChevronDown,
  FolderOpen,
  FolderClosed,
  Play,
  Edit,
  Trash2,
  Plus,
  AlertTriangle,
  MoreVertical,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { projectRollup, projectDeletionSet } from '@trackmind/core'
import { projectStatusMeta } from '@/lib/project-status'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useProjects } from '@/hooks/useProjects'
import type { Project, Task } from '@/types'
import { cn } from '@/lib/utils'

interface ProjectTreeProps {
  tasks: Task[]
  onSelectProject: (projectId: string) => void
  onEditProject: (project: Project) => void
  onCreateSubproject: (parentId: string) => void
}

interface ProjectNodeProps {
  project: Project
  /** All projects, so a node can roll up its own branch. */
  allProjects: Project[]
  tasks: Task[]
  isExpanded: boolean
  onToggle: () => void
  onSelect: () => void
  onEdit: () => void
  onDelete: () => Promise<void>
  onCreateSubproject: () => void
  onUnpauseSubprojects: () => Promise<void>
  childProjects: Project[]
  isParentPaused: boolean
  hasChildProjects: boolean
  hasPausedSubprojects: boolean
}

function ProjectNode({
  project,
  allProjects,
  tasks,
  isExpanded,
  onToggle,
  onSelect,
  onEdit,
  onDelete,
  onCreateSubproject,
  onUnpauseSubprojects,
  childProjects,
  isParentPaused,
  hasChildProjects,
  hasPausedSubprojects,
}: ProjectNodeProps) {
  const [deleting, setDeleting] = useState(false)
  const [unpausingAll, setUnpausingAll] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  // Rolled up over the whole branch, so a folder holding nothing itself but
  // twenty tasks two levels down doesn't read as empty when it's collapsed.
  const rollup = projectRollup(allProjects, tasks, project.id)
  const completedCount = rollup.completed
  const totalCount = rollup.total
  const completionRate = rollup.progress

  // What the delete will actually remove, from the same closure the delete
  // itself uses — so the dialog cannot promise a different number again.
  const deletion = projectDeletionSet(allProjects, tasks, project.id)

  const isPaused = project.status === 'paused'
  const isEffectivelyPaused = isPaused || isParentPaused
  // Every status, not just the two that used to have a branch here.
  const status = projectStatusMeta(project.status)

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await onDelete()
      setShowDeleteConfirm(false)
    } finally {
      setDeleting(false)
    }
  }

  const handleUnpauseSubprojects = async () => {
    setUnpausingAll(true)
    try {
      await onUnpauseSubprojects()
    } finally {
      setUnpausingAll(false)
    }
  }

  return (
    <div className="group">
      <div
        className={cn(
          "flex items-center gap-2 py-2 px-3 rounded-lg transition-all cursor-pointer",
          "hover:bg-accent/50",
          (isEffectivelyPaused || status.dim) && "opacity-70"
        )}
      >
        {/* Expand/Collapse button */}
        {hasChildProjects ? (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onToggle()
            }}
            className="p-0.5 hover:bg-accent rounded"
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        ) : (
          <span className="w-5" />
        )}

        {/* Folder icon */}
        {isExpanded && hasChildProjects ? (
          <FolderOpen className={cn('h-5 w-5', status.dim ? 'text-muted-foreground' : 'text-amber-500')} />
        ) : (
          <FolderClosed className={cn('h-5 w-5', status.dim ? 'text-muted-foreground' : 'text-amber-500')} />
        )}

        {/* Project name and info */}
        <div className="flex-1 min-w-0" onClick={onSelect}>
          <div className="flex flex-wrap items-center gap-2 md:flex-nowrap">
            <span
              className={cn(
                'font-medium truncate text-sm md:text-base',
                // Matches the grid view, which struck completed names through
                // while this view left them looking live.
                status.strike && 'line-through text-muted-foreground',
              )}
            >
              {project.name}
            </span>

            {/* Status badges. Below md these become the icon alone — three
                words of badge in a 390px row leave nothing for the project's
                name, which is the only part you're scanning for.

                Every non-active status gets one. Previously only `paused` did,
                so a completed or archived project was indistinguishable from a
                live one on a phone (where the `active` badge is hidden too). */}
            {status.Icon && (
              <Badge
                variant="outline"
                className={cn('text-xs px-1 md:px-2', status.badgeClass)}
                title={`This project is ${status.label}`}
              >
                <status.Icon className="h-3 w-3 md:mr-1" />
                <span className="hidden md:inline">{status.label}</span>
              </Badge>
            )}
            {isParentPaused && !isPaused && (
              <Badge
                variant="outline"
                className="text-orange-500 border-orange-400 text-xs cursor-help px-1 md:px-2"
                title="Paused because parent project is paused"
              >
                <AlertTriangle className="h-3 w-3 md:mr-1" />
                <span className="hidden md:inline">inherited</span>
              </Badge>
            )}
            {/* "active" is the norm, so on a phone it's noise: the absence of a
                status icon says the same thing in no space at all. */}
            {project.status === 'active' && !isParentPaused && (
              <Badge variant="outline" className="hidden md:inline-flex text-green-600 border-green-500 text-xs">
                active
              </Badge>
            )}

            {/* Task count */}
            {totalCount > 0 && (
              <span className="text-xs text-muted-foreground tabular-nums">
                {completedCount}/{totalCount}
                <span className="hidden md:inline"> tasks</span>
              </span>
            )}

            {/* Sub-project count — the chevron already says there are some. */}
            {childProjects.length > 0 && (
              <span className="hidden md:inline text-xs text-muted-foreground">
                {childProjects.length} sub-project{childProjects.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* Progress bar */}
          {totalCount > 0 && (
            <div className="mt-1 max-w-xs">
              <Progress value={completionRate} className="h-1" />
            </div>
          )}
        </div>

        {/* Phone: one menu. Four icon buttons next to a project name in 390px
            leaves about eight characters for the name. */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 md:hidden">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            {hasPausedSubprojects && (
              <DropdownMenuItem
                onClick={(e) => { e.stopPropagation(); handleUnpauseSubprojects() }}
                disabled={unpausingAll}
              >
                <Play className="h-4 w-4 mr-2 text-green-600" />
                Unpause sub-projects
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onCreateSubproject() }}>
              <Plus className="h-4 w-4 mr-2" />
              New sub-project
            </DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit() }}>
              <Edit className="h-4 w-4 mr-2" />
              Edit project
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive"
              onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(true) }}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Desktop: the icon strip, hover-revealed as before. */}
        <div className="hidden md:flex items-center gap-1 transition-opacity md:opacity-0 md:group-hover:opacity-100">
          {/* Unpause all sub-projects button */}
          {hasPausedSubprojects && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={(e) => {
                e.stopPropagation()
                handleUnpauseSubprojects()
              }}
              disabled={unpausingAll}
              title="Unpause all sub-projects"
            >
              <Play className="h-4 w-4 text-green-600" />
            </Button>
          )}

          {/* Add sub-project button */}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={(e) => {
              e.stopPropagation()
              onCreateSubproject()
            }}
            title="Create sub-project"
          >
            <Plus className="h-4 w-4" />
          </Button>

          {/* Edit button */}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={(e) => {
              e.stopPropagation()
              onEdit()
            }}
          >
            <Edit className="h-4 w-4" />
          </Button>

          {/* Delete button with confirmation */}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-destructive hover:text-destructive"
            onClick={(e) => {
              e.stopPropagation()
              setShowDeleteConfirm(true)
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>

          {/* Delete confirmation dialog */}
          <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete Project</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete "{project.name}"?
                  {/* Counts come from the same closure the delete actually
                      uses. They used to be `childProjects.length` (direct
                      children only, not the subtree) and the rollup's
                      `totalCount` (which excludes habits and archived work)
                      — so the dialog could promise to delete four tasks and
                      remove seven, or none at all. */}
                  {deletion.subprojectCount > 0 && (
                    <span className="block mt-2 text-destructive font-medium">
                      This will also delete {deletion.subprojectCount} sub-project{deletion.subprojectCount !== 1 ? 's' : ''}.
                    </span>
                  )}
                  {deletion.taskCount > 0 && (
                    <span className="block mt-1">
                      {deletion.taskCount} task{deletion.taskCount !== 1 ? 's' : ''} and any subtasks will be deleted. This cannot be undone.
                    </span>
                  )}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  )
}

export function ProjectTree({
  tasks,
  onSelectProject,
  onEditProject,
  onCreateSubproject,
}: ProjectTreeProps) {
  const {
    projects,
    topLevelProjects,
    isParentPaused,
    getChildProjects,
    deleteProject,
    unpauseAllSubprojects,
  } = useProjects()

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  // Build tree structure
  const buildTree = (parentId: string | null = null, level: number = 0): React.JSX.Element[] => {
    const projectsAtLevel = parentId === null
      ? topLevelProjects
      : getChildProjects(parentId)

    return projectsAtLevel.map((project) => {
      const childProjects = getChildProjects(project.id)
      const hasChildren = childProjects.length > 0
      const isExpanded = expandedIds.has(project.id)
      const parentPaused = isParentPaused(project.id)

      // Check if any sub-project is paused
      const hasPausedSubprojects = childProjects.some(
        (child) => child.status === 'paused'
      ) || childProjects.some((child) =>
        getChildProjects(child.id).some((grandchild) => grandchild.status === 'paused')
      )

      return (
        <div key={project.id}>
          <ProjectNode
            project={project}
            allProjects={projects}
            tasks={tasks}
            isExpanded={isExpanded}
            onToggle={() => {
              setExpandedIds((prev) => {
                const next = new Set(prev)
                if (next.has(project.id)) {
                  next.delete(project.id)
                } else {
                  next.add(project.id)
                }
                return next
              })
            }}
            onSelect={() => onSelectProject(project.id)}
            onEdit={() => onEditProject(project)}
            onDelete={() => deleteProject(project.id)}
            onCreateSubproject={() => onCreateSubproject(project.id)}
            onUnpauseSubprojects={() => unpauseAllSubprojects(project.id)}
            childProjects={childProjects}
            isParentPaused={parentPaused}
            hasChildProjects={hasChildren}
            hasPausedSubprojects={hasPausedSubprojects}
          />

          {/* Children indent by nesting the wrapper, so each level compounds.
              The old indent lived on the node itself as a boolean (level > 0),
              which gave depth 1 and depth 4 the *same* single step — a chain
              p2 → p3 → p4 rendered flat and read as siblings. The guide line
              rides the wrapper too, so every generation draws its own rail.
              Phones get a smaller step: at 24px/level a five-deep chain would
              spend a third of a 390px screen on margins. */}
          {isExpanded && hasChildren && (
            <div className="mt-1 ml-3 border-l-2 border-muted pl-2 md:ml-5 md:pl-3">
              {buildTree(project.id, level + 1)}
            </div>
          )}
        </div>
      )
    })
  }

  // Expand/collapse all
  const expandAll = () => {
    const allIds = new Set(projects.map((p) => p.id))
    setExpandedIds(allIds)
  }

  const collapseAll = () => {
    setExpandedIds(new Set())
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2">
            <FolderOpen className="h-5 w-5" />
            Project Tree
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={expandAll}>
              Expand All
            </Button>
            <Button variant="ghost" size="sm" onClick={collapseAll}>
              Collapse All
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {projects.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">
            No projects yet. Create your first project to get started.
          </p>
        ) : (
          <div className="space-y-1">
            {buildTree()}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
